// External library imports
import * as yup from 'yup';
import { get as lodashGet, set as lodashSet } from 'lodash';
// Internal module import
import { ERRORS } from '../../utils/validationHelper';

const checkMultiples = (sum) => {
    const remainder = sum % 0.1;
    const tolerance = 0.00001;
    return remainder <= tolerance || Math.abs(sum - Math.round(sum * 10) / 10) <= tolerance;
};

const capacityValidate = (market, approvedCapacity) => {
    if (market.includes('aFRR') || market.includes('mFRR'))
        return yup.array().of(
            yup
                .number()
                .min(0, 'Capacity should be greater than 0')
                .max(approvedCapacity, 'Capacity should be less than or equal to approved capacity')
                .required(ERRORS.required)
                .test('multiple of 1', 'Capacity should be multiple of 1', (val, context) => {
                    if (!Number.isInteger(val)) return false;
                    if (market.includes('mFRR')) {
                        const capacity = context?.parent || [];
                        const allCap = capacity.filter((cap) => cap > 0);
                        const allSame = allCap.every((cap) => cap === allCap[0]);

                        if (!allSame) {
                            return context.createError({
                                path: context.path,
                                message: 'Capacity must be the same or 0',
                            });
                        } else return true;
                    } else return true;
                })
        );
    else
        return yup
            .array()
            .of(
                yup
                    .number()
                    .min(0, 'Capacity should be greater than 0')
                    .max(approvedCapacity, 'Capacity should be less than or equal to approved capacity')
                    .required(ERRORS.required)
            )
            .required(ERRORS.required);
};

const validatePrice = (type = 'capacityPrice') => {
    return yup.array().of(
        yup
            .number()
            .required(ERRORS.required)
            .min(0)
            .test('price', 'Price should be greater than 0', (val, c) => {
                const idx = lodashGet(c, ['options', 'index']);
                if (lodashGet(c, ['from', 0, 'value', 'capacity', idx], 0) > 0 && val <= 0) return false;
                else if (lodashGet(c, ['from', 0, 'value', 'capacity', idx], 0) <= 0 && val !== 0) {
                    const { path, createError } = c;
                    return createError({
                        path,
                        message: 'Price should be 0',
                    });
                } else {
                    if (type === 'capacityPrice') {
                        const allPrices = c?.parent?.filter((p) => p > 0);
                        const allSame = allPrices.every((p) => p === allPrices[0]);
                        if (!allSame) {
                            return c.createError({
                                path: c.path,
                                message: 'Price must be the same',
                            });
                        } else return true;
                    } else return true;
                }
            })
    );
};

const bundleBidCapacityValidate = (market, approvedCapacity, pairs) => {
    let temp;
    const validate = {};
    if (market.includes('aFRR') || market.includes('mFRR'))
        temp = yup.array().of(
            yup
                .number()
                .min(0, 'Capacity should be greater than 0')
                .required(ERRORS.required)
                .test('approved-capacity-test', 'Capacity should be less than or equal to approved capacity', (value, context) => {
                    const { path } = context;
                    const instanceId = path.split('.')[1].split('[')[0];
                    if (value > lodashGet(approvedCapacity, [instanceId], 0)) return false;
                    else return true;
                })
                .test('multiple of 1', 'Capacity should be multiple of 1', (__, context) => {
                    const capacity = lodashGet(context, ['from', 0, 'value']);
                    const idx = lodashGet(context, ['options', 'index']);
                    let sum = 0;
                    for (let key in capacity) {
                        sum += Number(lodashGet(capacity, [key, idx]));
                    }

                    if (!Number.isInteger(sum)) return false;
                    if (market.includes('mFRR')) {
                        const sumArray = [];
                        for (let key in capacity) {
                            const length = context?.parent?.length;
                            Array(length)
                                .fill(null)
                                .map((__, idx) => {
                                    const tempSum = lodashGet(sumArray, [idx], 0) + Number(lodashGet(capacity, [key, idx]));
                                    lodashSet(sumArray, [idx], tempSum);
                                });
                        }
                        const allCap = sumArray.filter((cap) => cap > 0);
                        const allSame = allCap.every((cap) => cap === allCap[0]);

                        if (!allSame) {
                            return context.createError({
                                path: context.path,
                                message: 'Capacity must be the same or 0',
                            });
                        } else return true;
                    } else return true;
                })
        );
    else
        temp = yup.array().of(
            yup
                .number()
                .min(0, 'Capacity should be greater than 0')
                .test('approved-capacity-test', 'Capacity should be less than or equal to approved capacity', (value, context) => {
                    const { path } = context;
                    const instanceId = path.split('.')[1].split('[')[0];
                    if (value > lodashGet(approvedCapacity, [instanceId], 0)) return false;

                    return true;
                })
                .required(ERRORS.required)
                .test('modulo-test', 'Sum of capacity will be multiple of 0.1', (__, context) => {
                    const capacity = lodashGet(context, ['from', 0, 'value']);
                    const idx = lodashGet(context, ['options', 'index']);
                    let sum = 0;
                    for (let key in capacity) {
                        sum += Number(lodashGet(capacity, [key, idx]));
                    }
                    return checkMultiples(sum);
                })
        );

    pairs.map((e) => {
        lodashSet(validate, [e.instanceId], temp);
    });

    return validate;
};

const validateBundleBidPrice = (type = 'capacityPrice') => {
    return yup.array().of(
        yup
            .number()
            .required(ERRORS.required)
            .min(0)
            .test('price', 'Price should be greater than 0', (val, c) => {
                const idx = lodashGet(c, ['options', 'index']);
                const capacity = lodashGet(c, ['from', 0, 'value', 'capacity']);
                let capSum = 0;
                for (let key in capacity) {
                    capSum += Number(lodashGet(capacity, [key, idx]));
                }
                if (capSum > 0 && val <= 0) return false;
                else if (capSum <= 0 && val !== 0) {
                    const { path, createError } = c;
                    return createError({
                        path,
                        message: 'Price should be 0',
                    });
                } else {
                    if (type === 'capacityPrice') {
                        const allPrices = c?.parent?.filter((p) => p > 0);
                        const allSame = allPrices.every((p) => p === allPrices[0]);
                        if (!allSame) {
                            return c.createError({
                                path: c.path,
                                message: 'Price must be the same',
                            });
                        } else return true;
                    } else return true;
                }
            })
    );
};

export const CreateBidValidator = (market, approvedCapacity, hidePrice = false) => {
    const wrapper = {
        isExternal: yup.boolean().required(),
        reserveBidIdentification: ['FFR'].includes(market) ? yup.string().required(ERRORS.required) : yup.string().optional().nullable(),
        capacity: capacityValidate(market, approvedCapacity),
        price: hidePrice ? yup.array().of(yup.number().optional()) : validatePrice('capacityPrice'),
        ...(market.includes('aFRR') || market.includes('mFRR')
            ? {
                  regulationPrice: validatePrice('regulationPrice'),
              }
            : {}),
    };
    return yup.object().shape(wrapper);
};

export const ResultIdsValidator = () => {
    const wrapper = {
        reserveBidIdentification: yup.string().required(ERRORS.required),
        primaryResultMessageId: yup.string().optional(),
        secondaryResultMessageId: yup.string().optional(),
    };
    return yup.object().shape(wrapper);
};

export const SecondaryBidValidator = (primaryBidStatus, approvedCapacity) => {
    const wrapper = {
        capacity: yup.array().of(
            yup.mixed().test({
                name: 'isNotNull',
                message: ERRORS.required,
                test: (value, c) => {
                    const combinedCap = lodashGet(primaryBidStatus, [c.options.index, 'capacity'], 0) + value;

                    if (value < 0 && value < -approvedCapacity)
                        return c.createError({
                            message: `Capacity should be less than or equal to approved capacity.`,
                            path: c.path,
                        });

                    if (value === null || (value && combinedCap <= approvedCapacity) || value == 0) return true;
                    else if (combinedCap > approvedCapacity) {
                        return c.createError({
                            message: `Capacity should be less than or equal to approved capacity.`,
                            path: c.path,
                        });
                    } else return false;
                },
            })
        ),
        price: yup.array().of(
            yup.mixed().test({
                name: 'isNotNull',
                message: ERRORS.required,
                test: (value, c) => {
                    const capacity = lodashGet(c, ['from', 0, 'value', 'capacity'], []);
                    const idx = lodashGet(c, ['options', 'index']);
                    if ((lodashGet(capacity, [idx], 0) > 0 && value > 0) || value === null) return true;
                    else {
                        const { path, createError } = c;
                        if (lodashGet(capacity, [idx], 0) > 0 && value <= 0)
                            return createError({
                                path,
                                message: 'Price should be greater than 0',
                            });
                        else if (lodashGet(capacity, [idx], 0) <= 0 && value !== 0)
                            return createError({
                                path,
                                message: 'Price should be 0',
                            });
                        else if (value === null || value >= 0) return true;
                        else return false;
                    }
                },
            })
        ),
    };

    return yup.object().shape(wrapper);
};

export const CreateNoteValidator = () => {
    const wrapper = {
        description: yup.string().required(ERRORS.required),
    };
    return yup.object().shape(wrapper);
};

export const updatePostedEnergyBidRegulationPrice = (selectedHours = [], status) => {
    const regulationPriceShape = {};

    selectedHours.map((h) => {
        lodashSet(regulationPriceShape, h, yup.number().min(0).required(ERRORS.required));
    });

    const wrapper = {
        regulationPrice: status === 'active' ? yup.object().shape(regulationPriceShape) : yup.object().shape({}),
        status: yup.string().required(ERRORS.required),
    };

    return yup.object().shape(wrapper);
};

export const CreateBundleBidValidator = (market, approvedCapacity, pairs, hidePrice = false) => {
    const wrapper = {
        isExternal: yup.boolean().required(),
        reserveBidIdentification: ['FFR'].includes(market) ? yup.string().required(ERRORS.required) : yup.string().optional().nullable(),
        capacity: yup.object().shape(bundleBidCapacityValidate(market, approvedCapacity, pairs)),
        price: hidePrice ? yup.array().of(yup.number().optional()) : validateBundleBidPrice('capacityPrice'),
        ...(market.includes('aFRR') || market.includes('mFRR')
            ? {
                  regulationPrice: validateBundleBidPrice('regulationPrice'),
              }
            : {}),
    };
    return yup.object().shape(wrapper);
};

export const SecondaryBundleBidValidator = (indivisualBidStatus, approvedCapacity, pairs) => {
    const bundleBidSecondaryCapacity = (indivisualBidStatus = {}, approvedCapacity = {}, pairs) => {
        let temp = {};
        pairs.map((e) => {
            temp[e.instanceId] = yup.array().of(
                yup
                    .mixed()
                    .test({
                        name: 'isNotNull',
                        message: ERRORS.required,
                        test: (value, c) => {
                            const combinedCap = lodashGet(indivisualBidStatus, [e.instanceId, c.options.index, 'capacity'], 0) + value;

                            if (value < 0 && value < -lodashGet(approvedCapacity, [e.instanceId], 0))
                                return c.createError({
                                    message: `Capacity should be less than or equal to approved capacity.`,
                                    path: c.path,
                                });

                            if (value === null || (value && combinedCap <= lodashGet(approvedCapacity, [e.instanceId], 0)) || value == 0) return true;
                            else if (combinedCap > lodashGet(approvedCapacity, [e.instanceId], 0))
                                return c.createError({
                                    message: `Capacity should be less than or equal to approved capacity.`,
                                    path: c.path,
                                });
                            else return false;
                        },
                    })
                    .test('modulo-test', 'Sum of capacity will be multiple of 0.1', function (value, context) {
                        const capacity = lodashGet(context, ['from', 0, 'value']);
                        const idx = lodashGet(context, ['options', 'index']);
                        let sum = 0;
                        for (let key in capacity) {
                            if (lodashGet(capacity, [key, idx]) > 0) sum += Number(lodashGet(capacity, [key, idx]));
                        }
                        return checkMultiples(sum);
                    })
            );
        });
        return temp;
    };

    const wrapper = {
        capacity: yup.object().shape(bundleBidSecondaryCapacity(indivisualBidStatus, approvedCapacity, pairs)),
        price: yup.array().of(
            yup.mixed().test({
                name: 'isNotNull',
                message: ERRORS.required,
                test: (value, c) => {
                    const capacity = lodashGet(c, ['from', 0, 'value', 'capacity'], {});
                    const idx = lodashGet(c, ['options', 'index']);
                    let isCapacity = false;
                    for (let key in capacity) {
                        if (lodashGet(capacity, [key, idx], 0) > 0) isCapacity = true;
                    }
                    // if (isCapacity && value > 0) return true;
                    if (isCapacity && value > 0) {
                        const allPrices = c?.parent?.filter((p) => p > 0);
                        const allSame = allPrices.every((p) => p === allPrices[0]);
                        if (!allSame) {
                            return c.createError({
                                path: c.path,
                                message: 'Price must be the same',
                            });
                        } else return true;
                    } else {
                        const { path, createError } = c;
                        if (isCapacity && value <= 0)
                            return createError({
                                path,
                                message: 'Price should be greater than 0',
                            });
                        else if (!isCapacity && value > 0)
                            return createError({
                                path,
                                message: 'Price should be 0',
                            });
                        else if (value === null || value >= 0) return true;
                        else return false;
                    }
                },
            })
        ),
    };

    return yup.object().shape(wrapper);
};

export const InstanceBundleValidation = () => {
    const wrapper = {
        name: yup.string().required(),
        market: yup.string().required(),
        pairs: yup
            .array()
            .of(
                yup.object().shape({
                    licenseId: yup.string().required(ERRORS.required),
                    instanceId: yup.string().required(ERRORS.required),
                    customerId: yup.string().required(ERRORS.required),
                })
            )
            .min(1)
            .required(ERRORS.required),
    };
    return yup.object().shape(wrapper);
};
